import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useDebouncedCallbackPerKey from 'hooks/useDebounceCallbackPerKey';
import InputNumber from 'layout/atoms/InputNumber';
import Loader from 'layout/atoms/Loader/Loader';
import Switch from 'layout/atoms/Switch/Switch';
import TableGrid, { Body, Cell, Head, Row } from 'layout/atoms/TableGrid/TableGrid';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import Grid from 'layout/molecules/Grid/Grid';
import { fetchWarehouseReleaseComponents, selectWarehouseReleaseComponents, selectWarehouseReleaseDetailsItem, selectWarehouseReleaseDetailsRefetchFlags, selectWarehouseReleaseDetailsStatus, selectWarehouseReleaseModel, setWarehouseReleaseComponentVariantConsumptionOverride, setWarehouseReleaseComponentVariantToNotReady, setWarehouseReleaseComponentVariantToReady, updateWarehouseReleaseComponentVariantStore, } from 'store/warehouseReleaseDetailsSlice';
const columns = [
    ['100px', '14%'],
    ['150px', '20%'],
    ['150px', '18%'],
    ['150px', '18%'],
    ['100px', '10%'],
    ['120px', '12%'],
    ['80px', '8%'],
];
function Components() {
    const { t } = useTranslation(['warehouseReleases', 'common']);
    const dispatch = useDispatch();
    const status = useSelector(selectWarehouseReleaseDetailsStatus);
    const refetchFlags = useSelector(selectWarehouseReleaseDetailsRefetchFlags);
    const warehouseReleaseDetails = useSelector(selectWarehouseReleaseDetailsItem);
    const warehouseReleaseModel = useSelector(selectWarehouseReleaseModel);
    const warehouseReleaseComponents = useSelector(selectWarehouseReleaseComponents);
    useEffect(() => {
        if (status.fetchComponents === 'idle' || refetchFlags.fetchComponents) {
            dispatch(fetchWarehouseReleaseComponents(warehouseReleaseDetails.id));
        }
    }, [dispatch, status, refetchFlags, warehouseReleaseDetails]);
    const [debouncedUpdateComponentVariants, savingKeys] = useDebouncedCallbackPerKey((name, value) => {
        dispatch(setWarehouseReleaseComponentVariantConsumptionOverride({
            warehouseReleaseComponentVariantId: name,
            value,
        }));
    }, 1000);
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        dispatch(updateWarehouseReleaseComponentVariantStore({
            warehouseReleaseComponentVariantId: name,
            value,
        }));
        debouncedUpdateComponentVariants(name, value);
    };
    const handleSwitchChange = (value, id) => {
        if (value) {
            dispatch(setWarehouseReleaseComponentVariantToNotReady(id));
        }
        else {
            dispatch(setWarehouseReleaseComponentVariantToReady(id));
        }
    };
    if (warehouseReleaseModel === null) {
        return (React.createElement(Grid, null,
            React.createElement(EmptyState, { header: t('warehouse_release_volumen_list_placeholder_header'), subheader: t('warehouse_release_volumen_list_placeholder_subheader_no_model') })));
    }
    if (!warehouseReleaseModel.ready) {
        return (React.createElement(Grid, null,
            React.createElement(EmptyState, { header: t('warehouse_release_volumen_list_placeholder_header'), subheader: t('warehouse_release_volumen_list_placeholder_subheader') })));
    }
    if (status.fetchComponents === 'idle' || status.fetchComponents === 'loading') {
        return (React.createElement(Grid, null,
            React.createElement(Loader, null)));
    }
    return (React.createElement("div", { style: { position: 'relative' } },
        React.createElement(Grid, null, warehouseReleaseComponents.components.map((component) => (React.createElement("div", { key: component.id },
            React.createElement("h3", null, component.componentCategory.typeLabelPL),
            React.createElement(TableGrid, { columns: columns },
                React.createElement(Head, null,
                    React.createElement(Row, { head: true },
                        React.createElement(Cell, null, t('component_supplier')),
                        React.createElement(Cell, null, t('component_name')),
                        React.createElement(Cell, null, t('component_params')),
                        React.createElement(Cell, null, t('component_variant_params')),
                        React.createElement(Cell, null, t('calculated_volume')),
                        React.createElement(Cell, null, t('release_volume')),
                        React.createElement(Cell, null, t('common:done')))),
                React.createElement(Body, null, component.componentVariants.map((variant) => (React.createElement(Row, { key: variant.id },
                    React.createElement(Cell, null, variant.supplierName),
                    React.createElement(Cell, null, variant.name),
                    React.createElement(Cell, null, variant.componentAttributesPL),
                    React.createElement(Cell, null, variant.variantAttributesPL),
                    React.createElement(Cell, null, variant.calculatedConsumption),
                    React.createElement(Cell, null,
                        React.createElement(InputNumber, { name: variant.id, value: variant.consumptionOverride
                                ? variant.consumptionOverride
                                : variant.calculatedConsumption, disabled: variant.ready, onChange: handleInputChange, loading: savingKeys.has(variant.id), decimal: 2 })),
                    React.createElement(Cell, null, variant.canBeSetToReady && (React.createElement(Switch, { value: variant.ready, onChange: () => handleSwitchChange(variant.ready, variant.id) })))))))))))),
        status.add === 'loading' && React.createElement(Loader, { overlay: true })));
}
export default memo(Components);
